import { TRIP_STATES } from "./journeyState";
import { JOURNEY_UPDATE_CATEGORIES } from "./journeyUpdates";

export const ATTENTION_TYPES = {
  NOW: "NOW",
  NEXT: "NEXT",
  UPDATE: "UPDATE",
  PREPARATION: "PREPARATION",
  LOGISTICS: "LOGISTICS",
  DOCUMENT: "DOCUMENT",
  MESSAGE: "MESSAGE",
};

const VISIBLE_VALUES = new Set(["", "public", "trip", "participants"]);
const HIDDEN_VALUES = new Set(["operator", "private", "internal", "staff"]);

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function text(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function boolOrNull(value) {
  return typeof value === "boolean" ? value : null;
}

function isParticipantVisible(item) {
  const visibility = text(item?.visibility).toLowerCase();
  if (HIDDEN_VALUES.has(visibility)) return false;
  if (item?.participant_visible === false) return false;
  return VISIBLE_VALUES.has(visibility);
}

function activityTitle(activity) {
  return text(activity?.title || activity?.name) || "Untitled activity";
}

function activityLocation(activity) {
  return text(activity?.location_text || activity?.location || activity?.primary_location?.name);
}

function activitySummary(activity) {
  if (!activity) return null;
  return {
    activityId: text(activity.id),
    dayId: text(activity.day_id || activity.day?.id),
    dayNumber: activity.day_number ?? activity.dayNumber ?? activity.day?.day_number ?? null,
    title: activityTitle(activity),
    time: text(activity.start_time || activity.timing?.start_time),
    endTime: text(activity.end_time || activity.timing?.end_time),
    location: activityLocation(activity),
  };
}

function tripDestination(trip) {
  return text(trip?.destination || trip?.region);
}

function deriveToday(journeyState) {
  const day = journeyState?.todayDay;
  if (!day) return null;
  return {
    dayId: text(day.id),
    dayNumber: day.day_number ?? null,
    title: text(day.title) || (day.day_number ? `Day ${day.day_number}` : "Today"),
    date: text(day.date || day.timing?.date),
    totalDays: journeyState?.totalDays || null,
  };
}

function deriveProgress(progress) {
  if (!progress) return null;
  return {
    checkedInCount: numberOrNull(progress.checkedInCount) ?? 0,
    eligibleActivityCount: numberOrNull(progress.eligibleActivityCount) ?? 0,
    percent: numberOrNull(progress.percent),
    complete: progress.eligibleActivityCount > 0 && progress.checkedInCount >= progress.eligibleActivityCount,
    lastCheckInAt: text(progress.lastCheckIn?.created_at || progress.lastCheckIn?.at),
  };
}

function latestUpdates(journeyUpdates) {
  const items = asArray(journeyUpdates?.items)
    .filter((item) => item?.category)
    .slice(0, 5)
    .map((item) => ({
      id: text(item.id),
      category: text(item.category),
      title: text(item.title) || "Journey updated",
      message: text(item.message),
      createdAt: text(item.createdAt),
      severity: text(item.severity) || "normal",
      source: text(item.source) || "notification",
      dayNumber: item.dayNumber ?? null,
      activityId: text(item.activityId),
    }));
  return {
    items,
    latestAt: text(journeyUpdates?.latestAt),
    unreadCount: numberOrNull(journeyUpdates?.unreadCount) ?? 0,
  };
}

function compareByTime(a, b) {
  return text(a?.departure_at || a?.check_in || a?.arrival_at).localeCompare(text(b?.departure_at || b?.check_in || b?.arrival_at));
}

function transportSummary(item) {
  if (!item || !isParticipantVisible(item)) return null;
  return {
    id: text(item.id),
    type: text(item.type),
    direction: text(item.direction),
    departureAt: text(item.departure_at),
    arrivalAt: text(item.arrival_at),
    status: text(item.status),
    label: text(item.label || item.summary || item.type || item.direction),
  };
}

function accommodationSummary(item) {
  if (!item || !isParticipantVisible(item)) return null;
  return {
    id: text(item.id),
    propertyName: text(item.property_name),
    type: text(item.type),
    address: text(item.address),
    checkIn: text(item.check_in),
    checkOut: text(item.check_out),
    status: text(item.status),
  };
}

function deriveLogistics(journey) {
  const transport = [
    ...asArray(journey?.logistics?.transportation),
    ...asArray(journey?.live?.transport_status),
  ].filter(isParticipantVisible).sort(compareByTime);
  const accommodations = asArray(journey?.logistics?.accommodations).filter(isParticipantVisible).sort(compareByTime);
  return {
    upcomingTransport: transportSummary(transport[0]),
    accommodation: accommodationSummary(accommodations[0]),
  };
}

function packingItems(journey) {
  const fromList = asArray(journey?.packing?.list);
  if (fromList.length) return fromList;
  return [...asArray(journey?.packing?.items), ...asArray(journey?.live?.packing_items)];
}

function derivePreparation(journey) {
  const summary = journey?.packing?.summary || {};
  const items = packingItems(journey);
  if (!items.length && !summary) {
    return { prepState: text(journey?.participant?.prep_state), packingSummary: null };
  }
  const total = numberOrNull(summary.total ?? summary.count) ?? items.length;
  const packed = numberOrNull(summary.packed) ?? items.filter((item) => item?.packed || item?.my_status?.packed || item?.provided_by_organizer).length;
  const requiredItems = items.filter((item) => item?.required && !item?.provided_by_organizer);
  const requiredPacked = numberOrNull(summary.required_packed) ?? requiredItems.filter((item) => item?.packed || item?.my_status?.packed).length;
  const requiredRemaining = numberOrNull(summary.required_remaining) ?? Math.max(0, requiredItems.length - requiredPacked);
  const remainingCount = numberOrNull(summary.remaining) ?? Math.max(0, total - packed);
  return {
    prepState: text(journey?.participant?.prep_state),
    packingSummary: total ? {
      totalItems: total,
      packedCount: packed,
      remainingCount,
      requiredRemaining,
      percent: numberOrNull(summary.percent),
      requiredPercent: numberOrNull(summary.required_percent),
      complete: remainingCount === 0 && requiredRemaining === 0,
    } : null,
  };
}

function deriveDocuments(journey) {
  const participantDocuments = asArray(journey?.participant_documents);
  const pending = participantDocuments.filter((doc) => ["requested", "under_review", "uploaded"].includes(text(doc.status))).length;
  if (!asArray(journey?.documents).length && !participantDocuments.length) return null;
  return {
    approvedTripDocumentCount: asArray(journey?.documents).length,
    participantDocumentCount: participantDocuments.length,
    pendingParticipantDocumentCount: pending,
  };
}

function deriveBooking(journey) {
  const booking = journey?.booking?.booking;
  if (!booking) return journey?.booking ? { status: "not_booked", paymentStatus: "" } : null;
  return {
    id: text(booking.id),
    state: text(booking.state),
    paymentStatus: text(booking.payment_status),
  };
}

function deriveLiveStatus(livePositionStatus) {
  if (!livePositionStatus) return { locationStatus: "UNKNOWN", locationActive: false, accuracyM: null };
  const status = text(livePositionStatus.status || livePositionStatus.locationStatus) || "UNKNOWN";
  const accuracyM = numberOrNull(livePositionStatus.accuracyM ?? livePositionStatus.accuracy);
  return {
    locationStatus: status,
    locationActive: boolOrNull(livePositionStatus.locationActive) ?? status === "ACTIVE",
    accuracyM: accuracyM === null ? null : Math.round(accuracyM),
  };
}

function attention(type, priority, title, message, source) {
  return { type, priority, title, message, source };
}

function deriveAttention(context) {
  const items = [];
  if (context.now) {
    items.push(attention(
      ATTENTION_TYPES.NOW,
      "normal",
      context.now.title,
      [context.now.time, context.now.location].filter(Boolean).join(" - "),
      { section: "journeyState", activityId: context.now.activityId },
    ));
  }
  if (context.next) {
    items.push(attention(
      ATTENTION_TYPES.NEXT,
      "normal",
      `Next: ${context.next.title}`,
      [context.next.time, context.next.location].filter(Boolean).join(" - "),
      { section: "journeyState", activityId: context.next.activityId },
    ));
  }
  const latestUpdate = context.updates.items[0];
  if (latestUpdate) {
    items.push(attention(
      latestUpdate.category === JOURNEY_UPDATE_CATEGORIES.MESSAGE ? ATTENTION_TYPES.MESSAGE : ATTENTION_TYPES.UPDATE,
      latestUpdate.severity === "urgent" ? "urgent" : latestUpdate.severity === "high" ? "high" : "normal",
      latestUpdate.title,
      latestUpdate.message,
      { section: "journeyUpdates", id: latestUpdate.id },
    ));
  }
  if (context.preparation?.packingSummary && context.preparation.packingSummary.requiredRemaining > 0) {
    items.push(attention(
      ATTENTION_TYPES.PREPARATION,
      "normal",
      "Packing still has required items",
      `${context.preparation.packingSummary.requiredRemaining} required item${context.preparation.packingSummary.requiredRemaining === 1 ? "" : "s"} remaining`,
      { section: "packing" },
    ));
  }
  if (context.logistics?.upcomingTransport) {
    const transport = context.logistics.upcomingTransport;
    items.push(attention(
      ATTENTION_TYPES.LOGISTICS,
      "normal",
      transport.label || transport.type || "Upcoming transport",
      [transport.departureAt, transport.status].filter(Boolean).join(" - "),
      { section: "transportation", id: transport.id },
    ));
  }
  if (context.documents?.pendingParticipantDocumentCount > 0) {
    items.push(attention(
      ATTENTION_TYPES.DOCUMENT,
      "normal",
      "Document status needs review",
      `${context.documents.pendingParticipantDocumentCount} participant document${context.documents.pendingParticipantDocumentCount === 1 ? "" : "s"} pending`,
      { section: "documents" },
    ));
  }
  return items.filter((item) => item.title).slice(0, 6);
}

export function deriveJourneyContext({
  journey,
  journeyState,
  journeyProgress,
  journeyUpdates,
  livePositionStatus,
} = {}) {
  const trip = journey?.trip || {};
  const tripState = journeyState?.tripState || trip.state || "UNKNOWN";
  const completed = tripState === TRIP_STATES.COMPLETED;
  const context = {
    trip: {
      id: text(trip.id),
      title: text(trip.title) || "TrailPulse trip",
      destination: tripDestination(trip),
      dates: {
        start: text(trip.start_date || trip.dates?.start),
        end: text(trip.end_date || trip.dates?.end),
      },
      tripState,
      scheduleKnown: !!journeyState?.tripScheduleKnown,
    },
    today: completed ? null : deriveToday(journeyState),
    now: completed ? null : activitySummary(journeyState?.nowActivity),
    next: completed ? null : activitySummary(journeyState?.nextActivity),
    progress: deriveProgress(journeyProgress),
    updates: latestUpdates(journeyUpdates),
    logistics: deriveLogistics(journey),
    preparation: derivePreparation(journey),
    documents: deriveDocuments(journey),
    booking: deriveBooking(journey),
    live: deriveLiveStatus(livePositionStatus),
    attention: [],
  };
  context.attention = deriveAttention(context);
  return context;
}
